/**
 * Landing copy and non-string content.
 *
 * Trilingual strings live in `web/messages/{uz,en,ru}.json` under the
 * `landing` key (T-WEB-03 moved them there, verbatim, from
 * `src/content/landing.js` — see `web/README.md` §Provenance). This module
 * re-assembles them into the same `CONTENT` shape the Vite build used, so
 * `Landing` and its sections keep importing `{ CONTENT, SCHOLARSHIPS }` from
 * one place instead of reaching into next-intl's message loader.
 *
 * The scholarship, nav-href and hero-highlight data below isn't
 * locale-dependent copy, so it stays as plain TS data rather than moving
 * into the message catalogs.
 */
import en from '../messages/en.json'
import ru from '../messages/ru.json'
import uz from '../messages/uz.json'

export const CONTENT = {
  uz: uz.landing,
  ru: ru.landing,
  en: en.landing,
}

export type Locale = keyof typeof CONTENT

/**
 * Featured scholarships. The design file carried flag emoji in `region` and
 * stripped them at render time (the design system bans emoji in product copy);
 * they are simply omitted here — the rendered text is identical.
 */
export const SCHOLARSHIPS = [
  { name: 'Chevening', region: "UK · Master's", deadline: '15 Nov', daysLeft: 24 },
  { name: 'Stipendium Hungaricum', region: "Hungary · Bachelor's+", deadline: '15 Jan', daysLeft: 61 },
  { name: 'Türkiye Bursları', region: 'Türkiye · All levels', deadline: '20 Feb', daysLeft: 97 },
]

/**
 * Programme names for the trust strip under the hero. Wordmarks, not logos:
 * the marks are the programmes' own trademarks and we have no licence to
 * reproduce them, and plain text keeps the strip weightless and translatable.
 *
 * Keep this list to programmes Uniap actually covers — it reads as a claim
 * about coverage.
 */
export const PROGRAMS = [
  'Chevening',
  'Erasmus Mundus',
  'Stipendium Hungaricum',
  'Türkiye Bursları',
  'DAAD',
  'Fulbright',
  'MEXT',
]

/** Section anchors, in the same order as `nav`. */
export const NAV_HREFS = ['#scholarships', '#how-it-works', '#faq']

/** The hero's featured scholarship preview card. */
export const HERO_HIGHLIGHT = {
  name: 'Chevening',
  level: "UK · Master's",
  requirements: [
    ['IELTS', '6.5+'],
    ['GPA', '3.2+'],
  ],
  noteDate: '15 Nov',
}
